import { Button, Form, message, Modal, Select, Spin, Switch, TableColumnProps, Tag } from "antd";
import {
  createUser,
  deleteUser,
  disableUser,
  enableUser,
  getUsers,
  updatePermissions,
  updateUser,
} from "../../services/users";
import { getOceanReports, sendBulletinToUser } from "../../services/reports";
import * as sRoles from "../../services/roles";
import { getRoles } from "../../services/roles";
import useModal from "../../hooks/useModal";
import { usePagination } from "../../hooks/usePagination";
import {
  IDENTITY_TYPES_TEXT,
  USER_TYPES_TEXT,
  USER_TYPES,
} from "../../constants/constants";
import { IUser } from "../../types/interfaces";
import { useEffect, useMemo, useState } from "react";
import DeleteButton from "../../components/general/deleteButton/deleteButton";
import EditButton from "../../components/general/editButton/editButton";
import { Country } from "country-state-city";
import useCountry from "../../hooks/useCountry";
import useInput from "../../hooks/useInput";
import { ValidateForm } from "../../utils/validateForm";
import useAuth from "../../hooks/useAuth";
import Permission from "../../components/general/permission/permission";
import useCustomUpload from "../../hooks/useUpload";
import { deleteFile, downloadFile } from "../../services/files";
import { Key, Mail } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generateUniqueID } from "web-vitals/dist/modules/lib/generateUniqueID";
import dayjs from "dayjs";

function useUsers() {
  const { onTableChange, tableParams, resetPagination } = usePagination();
  const nameFilterRaw = useInput("");
  const emailFilterRaw = useInput("");
  const companyFilterRaw = useInput("");
  const [nameDebounced, setNameDebounced] = useState("");
  const [emailDebounced, setEmailDebounced] = useState("");
  const [companyDebounced, setCompanyDebounced] = useState("");
  const [typeFilter, setTypeFilter] = useState<any>();
  const { countryOptions, handleSearchCountry, country, handleSelectCountry } =
    useCountry();

  // Debounce the text filters by 400ms to avoid a query per keystroke
  useEffect(() => {
    const t = setTimeout(() => setNameDebounced(nameFilterRaw.value), 400);
    return () => clearTimeout(t);
  }, [nameFilterRaw.value]);
  useEffect(() => {
    const t = setTimeout(() => setEmailDebounced(emailFilterRaw.value), 400);
    return () => clearTimeout(t);
  }, [emailFilterRaw.value]);
  useEffect(() => {
    const t = setTimeout(() => setCompanyDebounced(companyFilterRaw.value), 400);
    return () => clearTimeout(t);
  }, [companyFilterRaw.value]);

  // Expose filter objects compatible with <Input value/onChange>
  const nameFilter = { value: nameFilterRaw.value, onChange: nameFilterRaw.onChange };
  const emailFilter = { value: emailFilterRaw.value, onChange: emailFilterRaw.onChange };
  const companyFilter = { value: companyFilterRaw.value, onChange: companyFilterRaw.onChange };

  const { cleanUploads, handleUpload, handleFileChange, uploads } =
    useCustomUpload();

  const memoizedPagination = useMemo(
    () => ({
      current: tableParams.pagination.current,
      pageSize: tableParams.pagination.pageSize,
      sorter: tableParams.sorter,
    }),
    [
      tableParams.pagination.current,
      tableParams.pagination.pageSize,
      tableParams.sorter,
    ],
  );

  const memoizedFilters = useMemo(
    () => ({
      name: nameDebounced,
      email: emailDebounced,
      companyName: companyDebounced,
      country: country?.isoCode,
      type: typeFilter,
    }),
    [nameDebounced, emailDebounced, companyDebounced, country?.isoCode, typeFilter],
  );

  useEffect(() => {
    resetPagination();
  }, [memoizedFilters]);

  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [
      "users",
      memoizedPagination.pageSize,
      memoizedPagination.current,
      memoizedPagination.sorter?.field,
      memoizedPagination.sorter?.order,
      memoizedFilters.country,
      memoizedFilters.type,
      memoizedFilters.name,
      memoizedFilters.email,
      memoizedFilters.companyName,
    ],
    [
      memoizedPagination.pageSize,
      memoizedPagination.current,
      memoizedPagination.sorter?.field,
      memoizedPagination.sorter?.order,
      memoizedFilters.country,
      memoizedFilters.type,
      memoizedFilters.name,
      memoizedFilters.email,
      memoizedFilters.companyName,
    ],
  );

  const { data, error, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getUsers({
        page: memoizedPagination.current || 1,
        limit: memoizedPagination.pageSize || 10,
        field: memoizedPagination.sorter?.field,
        order: memoizedPagination.sorter?.order,
        country: memoizedFilters.country,
        type: memoizedFilters.type,
        name: memoizedFilters.name,
        email: memoizedFilters.email,
        companyName: memoizedFilters.companyName,
      }),
  });

  const userModal = useModal();
  const { userData, rolePermissions } = useAuth();

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: getRoles,
  });

  const dynamicRoleOptions = useMemo(() => {
    const baseOptions = [...USER_TYPES];
    const roleList = rolesData || [];
    
    // Add non-system custom roles to the dropdown
    roleList.forEach((r: any) => {
      // If it's not a known base option (by value check), we add it
      if (!baseOptions.some(opt => opt.value === r.name)) {
        baseOptions.push({
          value: r.name,
          label: r.name.charAt(0).toUpperCase() + r.name.slice(1),
        });
      }
    });

    return baseOptions;
  }, [rolesData]);

  const dynamicUserTypesFilter = useMemo(() => {
    return [
      { value: "", label: "Todos" },
      ...dynamicRoleOptions,
    ];
  }, [dynamicRoleOptions]);

  const [form] = Form.useForm();

  const [switchesLoading, setSwitchesLoading] = useState<any>({});

  const handleTypeFilter = (value: any) => {
    setTypeFilter(value);
  };

  const handleOk = async () => {
    let path;

    try {
      userModal.loading.start();
      const validForm = await ValidateForm(form);
      if (!validForm) {
        userModal.loading.stop();
        return;
      }

      let upload;

      upload = await handleUpload({
        name: "photo",
        category: "users",
        required: false,
        editing: userModal.editing,
      });

      const formValues = form.getFieldsValue();
      const { permissions, ...data } = formValues;

      let savedUser;
      if (userModal.editing) {
        savedUser = await updateUser({
          ...data,
          id: userModal.id,
          profilePicture:
            upload?.path || userModal?.current?.profilePicture || "",
        });
      } else {
        savedUser = await createUser({
          ...data,
          profilePicture: upload?.path || "",
        });
      }

      // Guardar accesos y vistas permitidas
      if (permissions !== undefined && savedUser?.id) {
         let finalPermissions = [...permissions];
         // Agregar permisos padre cuando hay sub-permisos (sin duplicados)
         if (finalPermissions.some((p: string) => p.includes("ui:map")) && !finalPermissions.includes("ui:map")) {
           finalPermissions.push("ui:map");
         }
         if (finalPermissions.some((p: string) => p.includes("ui:newsletter")) && !finalPermissions.includes("ui:newsletter")) {
           finalPermissions.push("ui:newsletter");
         }
         await updatePermissions(savedUser.id, finalPermissions);
      }

      message.success(userModal.editing ? "Usuario actualizado correctamente" : "Usuario creado correctamente");
      await refetch();
      handleCancel();
    } catch (error: Error | any) {
      console.log("error", error);
      if (error.message === "El archivo es requerido.") {
        message.error("La foto de perfil es requerida");
      } else if (error?.response?.data.trim() === "user already exists") {
        message.error("Ya existe un usuario con este correo");
      } else if (error?.response?.data.trim() === "User limit reached") {
        message.error(
          "Has alcanzado el limite de usuarios permitido por tu suscripción",
        );
      } else {
        message.error(
          "Ha ocurrido un error inesperado, por favor intenta de nuevo",
        );
      }
      if (path) {
        await deleteFile(path);
      }
    } finally {
      userModal.loading.stop();
    }
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const special = "!@#$%^&*()_+";
    let pass = "";
    pass += chars[Math.floor(Math.random() * chars.length)];
    pass += upper[Math.floor(Math.random() * upper.length)];
    pass += nums[Math.floor(Math.random() * nums.length)];
    pass += special[Math.floor(Math.random() * special.length)];
    
    const all = chars + upper + nums + special;
    for (let i = 0; i < 6; i++) {
        pass += all[Math.floor(Math.random() * all.length)];
    }
    return pass.split('').sort(() => 0.5 - Math.random()).join('');
  };

  const handleOpenModal = () => {
    userModal.openModal();
    const generatedPassword = generatePassword();
    form.setFieldsValue({
      password: generatedPassword,
    });
    
    if (userData?.type === "empresa") {
      form.setFieldsValue({
        type: "empleado",
        companyName: userData?.name,
      });
    }
  };
  const handleCancel = () => {
    userModal.closeModal();
    userModal.setEditing(false);
    userModal.setId(undefined);
    userModal.setCurrent(undefined);
    form.resetFields();
    userModal.loading.stop();
    cleanUploads();
  };

  const handleDelete = async (id: number) => {
    try {
      //startLoading();
      await deleteUser(id);
      await refetch();
    } catch (error: any) {
      message.error(
        "Ha ocurrido un error inesperado, por favor intenta de nuevo",
      );
    }
  };

  const handleEdit = async (user: IUser) => {
    let uri;
    if (user.profilePicture) {
      uri = await downloadFile(user?.profilePicture, "image");
    }
    userModal.openModal();
    userModal.setEditing(true);
    userModal.setId(user.id);
    userModal.setCurrent({ ...user, url: uri });
    form.setFieldsValue({
      ...user,
      // @ts-ignore - edges typing needs backend DTO update
      permissions: (user?.edges as any)?.permissions?.map((p: any) => p.name) || [],
      companyName: user?.edges?.belongsToCompany?.[0]?.name || "",
      companyWebsite: user?.edges?.belongsToCompany?.[0]?.website || "",
      planId: user?.plan?.id || undefined,
    });
  };

  const { mutate: enableUserMutation } = useMutation({
    mutationFn: (id: number) => enableUser(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousUserList: any = queryClient.getQueryData(queryKey);

      const newUserList = {
        data: [],
        total: previousUserList.total,
      };

      newUserList.data = data?.data?.map((item: IUser) => {
        if (item.id === id) {
          return {
            ...item,
            active: true,
          };
        }
        return item;
      });

      queryClient.setQueryData(queryKey, () => newUserList);

      return { previousUserList };
    },
  });

  const { mutate: disableUserMutation } = useMutation({
    mutationFn: (id: number) => disableUser(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousUserList: any = queryClient.getQueryData(queryKey);
      const newUserList = {
        data: [],
        total: previousUserList.total,
      };
      newUserList.data = data?.data?.map((item: IUser) => {
        if (item.id === id) {
          return {
            ...item,
            active: false,
          };
        }
        return item;
      });
      queryClient.setQueryData(queryKey, () => newUserList);

      return { previousUserList };
    },
  });

  const toggleSwitch = async (checked: boolean, id: number) => {
    try {
      setSwitchesLoading({
        [`switch-${id}`]: true,
      });
      if (checked) {
        enableUserMutation(id);
      } else {
        disableUserMutation(id);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSwitchesLoading({
        [`switch-${id}`]: false,
      });
    }
  };

  const tableColumns: TableColumnProps[] = [
    {
      title: "Tipo de usuario",
      dataIndex: "type",
      key: "type",
      sorter: true,
      render: (type: string) => {
        const text = USER_TYPES_TEXT[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : "—");
        let color = "default";
        if (type === "admin" || type === "super_admin") color = "red";
        else if (type === "empresa") color = "blue";
        else if (type === "gestor") color = "purple";
        else if (type === "empleado") color = "green";

        return (
          <Tag color={color} style={{ borderRadius: "12px", padding: "2px 10px", fontWeight: 500, fontSize: "12px" }}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: true,
    },
    {
      title: "Tipo de identificación",
      dataIndex: "identityType",
      key: "identityType",
      render: (identityType: string) => {
        return <div>{IDENTITY_TYPES_TEXT[identityType] || "—"}</div>;
      },
    },
    {
      title: "Identificación",
      dataIndex: "identityNumber",
      key: "identityNumber",
      render: (val: string) => <div>{val || "—"}</div>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
    },
    {
      title: "Pais",
      dataIndex: "country",
      key: "country",
      sorter: true,
      render: (country: string) => {
        let c = Country.getCountryByCode(country);
        return <div>{c ? c.name : ""}</div>;
      },
    },
    {
      title: "Teléfono",
      dataIndex: "phone",
      key: "phone",
      render: (_: string, record: any) => {
        if (!record.phone && !record.phonePrefix) return <div>—</div>;
        let country = Country.getCountryByCode(record.phonePrefix);
        let phoneCode = country
          ? `${country?.phonecode.includes("+") ? "" : "+"}${country?.phonecode}`
          : "";
        return <div>{(phoneCode + " " + (record.phone || "")).trim() || "—"}</div>;
      },
    },
    {
      title: "Empresa",
      dataIndex: "edges",
      key: "company",
      render: (edges: any, record: any) => {
        if (record.type === "empresa") {
          return <div>{record.name}</div>;
        }
        if (edges?.belongsToCompany?.length > 0) {
          return <div>{edges.belongsToCompany[0].name}</div>;
        }
        return <div>—</div>;
      },
    },
    {
      title: "Fecha Inicio",
      dataIndex: "edges",
      key: "startDate",
      render: (edges: any) => {
        const company = edges?.belongsToCompany?.[0];
        const date = company?.edges?.subscription?.startDate;
        if (date) {
          const d = new Date(date);
          if (d.getFullYear() > 1) return <div>{d.toLocaleDateString()}</div>;
        }
        return <div>—</div>;
      },
    },
    {
      title: "Fecha Fin",
      dataIndex: "edges",
      key: "endDate",
      render: (edges: any) => {
        const company = edges?.belongsToCompany?.[0];
        const date = company?.edges?.subscription?.endDate;
        if (date) {
          const d = new Date(date);
          if (d.getFullYear() > 1) return <div>{d.toLocaleDateString()}</div>;
        }
        return <div>—</div>;
      },
    },
    {
      title: "Días al corte",
      dataIndex: "edges",
      key: "daysLeft",
      render: (edges: any) => {
        const company = edges?.belongsToCompany?.[0];
        const date = company?.edges?.subscription?.endDate;
        if (date) {
          const d = dayjs(date);
          if (d.year() > 1) {
            const diff = d.diff(dayjs(), 'days');
            return <div>{diff < 0 ? "Expirado" : diff}</div>;
          }
        }
        return <div>—</div>;
      },
    },
    {
      title: "Plan Activo",
      dataIndex: "edges",
      key: "plan",
      render: (edges: any) => {
        const company = edges?.belongsToCompany?.[0];
        const planName = company?.edges?.subscription?.edges?.subscriptionPlan?.name;
        return <div>{planName || "—"}</div>;
      },
    },
    {
      title: "Acciones",
      dataIndex: "actions",
      key: "actions",
      render: (_: string, record: any) => {
        const isTargetAdmin = record.type === "admin" || record.type === "super_admin";
        const isCurrentUserAdmin = userData?.type === "admin" || userData?.type === "super_admin";
        
        if (isTargetAdmin && !isCurrentUserAdmin) {
          return <div className="actions"></div>;
        }

        return (
          <div className="actions">
            <Permission requiredPermission={`ui:users:disable`}>
              <Switch
                id={`switch-${record.id}`}
                onChange={(e: boolean) => toggleSwitch(e, record.id)}
                loading={switchesLoading[`switch-${record.id}`]}
                checked={record.active}
                disabled={record.id === userData?.id}
              />
            </Permission>
            <Permission requiredPermission={`ui:users:delete`}>
              <DeleteButton
                id={record.id}
                handleDelete={handleDelete}
                text={"Vas a eliminar un usuario, estas seguro?"}
              />
            </Permission>
            <Permission requiredPermission={`ui:users:update`}>
              <EditButton record={record} handleEdit={handleEdit} />
            </Permission>
            <Permission requiredPermission={`ui:bulletin:generate`}>
              <div
                title="Enviar boletín por email a este usuario"
                style={{
                  background: "#1890ff",
                  borderRadius: "50%",
                  width: 28, height: 28,
                  display: "flex", justifyContent: "center", alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setBulletinModalUser({ id: record.id, name: record.name, email: record.email });
                  setBulletinModalOpen(true);
                  setBulletinCountry("");
                  setBulletinSection("");
                  setBulletinFile("");
                  setBulletinFiles([]);
                }}
              >
                <Mail color="white" width={14} />
              </div>
            </Permission>
          </div>
        );
      },
    },
  ];

  // ── Send-bulletin-to-user modal state ────────────────────────────────────────────────
  const [bulletinModalOpen, setBulletinModalOpen] = useState(false);
  const [bulletinModalUser, setBulletinModalUser] = useState<{ id: number; name: string; email: string } | null>(null);
  const [bulletinCountry, setBulletinCountry] = useState("");
  const [bulletinSection, setBulletinSection] = useState("");
  const [bulletinFile, setBulletinFile] = useState("");
  const [bulletinFiles, setBulletinFiles] = useState<any[]>([]);
  const [bulletinFilesLoading, setBulletinFilesLoading] = useState(false);
  const [bulletinSending, setBulletinSending] = useState(false);

  // Fetch available files when country+section change
  const loadBulletinFiles = async (c: string, s: string) => {
    if (!c) { setBulletinFiles([]); return; }
    try {
      setBulletinFilesLoading(true);
      const data = await getOceanReports(c, s || undefined);
      const items = Array.isArray(data) ? data : (data?.bulletins || data?.items || data?.data || []);
      setBulletinFiles(items.map((f: any) => ({ label: f.filename || f.name, value: f.filename || f.name })));
    } catch {
      setBulletinFiles([]);
    } finally {
      setBulletinFilesLoading(false);
    }
  };

  const handleSendBulletinToUser = async () => {
    if (!bulletinModalUser || !bulletinCountry || !bulletinFile) {
      message.warning("Selecciona país y boletín");
      return;
    }
    try {
      setBulletinSending(true);
      const result = await sendBulletinToUser(bulletinCountry, bulletinFile, bulletinModalUser.id, "ocean");
      message.success(result.message || `Boletín enviado a ${bulletinModalUser.email}`);
      setBulletinModalOpen(false);
    } catch (err: any) {
      const detail = err?.response?.data?.error || "Error al enviar el boletín";
      message.error(detail);
    } finally {
      setBulletinSending(false);
    }
  };

  return {
    country,
    countryOptions,
    data: data?.data,
    error,
    form,
    handleCancel,
    handleFileChange,
    handleOk,
    handleOpenModal,
    handleSearchCountry,
    handleSelectCountry,
    handleTypeFilter,
    loading: isFetching,
    onTableChange,
    refetch,
    tableColumns,
    tableParams,
    total: data?.total,
    typeFilter,
    uploads,
    nameFilter,
    emailFilter,
    companyFilter,
    userModal,
    dynamicRoleOptions,
    dynamicUserTypesFilter,
    rolesLoading,
    // Bulletin modal
    bulletinModalOpen,
    setBulletinModalOpen,
    bulletinModalUser,
    bulletinCountry,
    setBulletinCountry,
    bulletinSection,
    setBulletinSection,
    bulletinFile,
    setBulletinFile,
    bulletinFiles,
    bulletinFilesLoading,
    bulletinSending,
    loadBulletinFiles,
    handleSendBulletinToUser,
  };
}

export default useUsers;
